import React, { useState } from 'react';
import { FileText, Trash2, Download, Eye, Bookmark, Calendar, Search, Plus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useContentPlanStore } from '../../stores/contentPlanStore';
import { useSavedIdeasStore } from '../../stores/savedIdeasStore';
import SavedPlanModal from './plans/SavedPlanModal';
import { sanitizeHtml } from '../../utils/sanitize';
import toast from 'react-hot-toast';

export default function SavedPlans() {
  const { plans, removePlan } = useContentPlanStore();
  const { savedIdeas, removeIdea } = useSavedIdeasStore();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleDownload = (content: string) => {
    const sanitizedContent = sanitizeHtml(content);
    const blob = new Blob([sanitizedContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plano-de-conteudo.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAddToCalendar = (planId: string) => {
    navigate('/dashboard/calendario', { 
      state: { showImportModal: true, selectedPlanId: planId }
    });
  };

  // Filter plans and ideas based on search term
  const filteredPlans = plans.filter(plan => 
    plan.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredIdeas = savedIdeas.filter(idea => 
    idea.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    idea.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Pesquisar planos e ideias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          />
        </div>
      </div>

      {/* Saved Ideas Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Bookmark className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Ideias Salvas</h2>
              <p className="text-gray-600">Ideias de conteúdo que você salvou para usar depois</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {savedIdeas.length} {savedIdeas.length === 1 ? 'ideia' : 'ideias'}
          </div>
        </div>

        {savedIdeas.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Bookmark className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma ideia salva ainda
            </h3>
            <p className="text-gray-600 mb-4">
              Suas ideias favoritas aparecerão aqui quando você salvá-las.
            </p>
            <button
              onClick={() => navigate('/dashboard/ideias')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Gerar Ideias
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredIdeas.map((idea, index) => (
              <div
                key={index}
                className="p-6 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 hover:shadow-md transition-all transform hover:scale-[1.02] relative"
              >
                <div className="absolute top-3 right-3">
                  <button
                    onClick={() => {
                      if (window.confirm('Remover esta ideia dos salvos?')) {
                        removeIdea(idea);
                        toast.success('Ideia removida dos salvos');
                      }
                    }}
                    className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                    title="Remover dos salvos"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{idea.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{idea.description}</p>
                  {idea.hashtags && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {idea.hashtags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-yellow-200 flex justify-between">
                  <span className="text-xs text-gray-500 capitalize">{idea.category}</span>
                  <span className="text-xs text-gray-500 capitalize">{idea.type}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content Plans Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Planos de Conteúdo</h2>
              <p className="text-gray-600">Gerencie seus planos de conteúdo salvos</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {plans.length} {plans.length === 1 ? 'plano' : 'planos'}
          </div>
        </div>

        {plans.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum plano salvo ainda
            </h3>
            <p className="text-gray-600 mb-4">
              Seus planos de conteúdo aparecerão aqui depois de salvos.
            </p>
            <button
              onClick={() => navigate('/dashboard/ideias')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Plano
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPlans.map((plan) => (
              <div
                key={plan.id}
                className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 hover:shadow-md transition-all transform hover:scale-[1.02]"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{plan.title}</h3>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleDownload(plan.content)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                      title="Baixar plano"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleAddToCalendar(plan.id)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                      title="Adicionar ao calendário"
                    >
                      <Calendar className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setSelectedPlan(plan.id)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                      title="Visualizar plano"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Tem certeza que deseja excluir este plano?')) {
                          removePlan(plan.id);
                          toast.success('Plano excluído com sucesso');
                        }
                      }}
                      className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                      title="Excluir plano"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>Criado em {formatDate(plan.createdAt)}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-indigo-100">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setSelectedPlan(plan.id)}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center"
                    >
                      Ver detalhes
                      <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleAddToCalendar(plan.id)}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center"
                    >
                      Adicionar ao calendário
                      <Calendar className="ml-1 h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPlan && (
        <SavedPlanModal
          planId={selectedPlan}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  );
}